import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useResourceUsageSummary } from "@/hooks/useResourceCredits";
import { grantAchievement } from "@/hooks/useGamification";
import { useLocale } from "@/hooks/useLocale";
import { getSessionUser } from "@/lib/auth-session";
import { useAuthReady } from "@/hooks/useAuthReady";

// ─── Types ───

export interface WorldItem {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  setting: string;
  banner_url?: string | null;
  allow_cloning?: boolean;
  is_public?: boolean;
  created_at: string;
  updated_at: string;
  user_id?: string;
  config?: any;
}

export interface SharedWorldItem extends WorldItem {
  permission: string;
  owner_name: string | null;
}

export interface PublicWorldItem extends WorldItem {
  owner_name: string | null;
  allow_cloning: boolean;
  is_own: boolean;
}

export interface AdventureItem {
  id: string;
  name: string;
  world_id: string;
  created_at: string;
  updated_at: string;
  state: any;
  player_character: any;
  is_multiplayer: boolean;
  invite_code: string | null;
  current_player_id: string | null;
  _player_count?: number;
}

export interface JoinedAdventureItem {
  id: string;
  adventure_id: string;
  user_id: string;
  is_host: boolean;
  character_name: string;
  adventure: {
    id: string;
    name: string;
    world_id: string;
    updated_at: string;
    state: any;
    is_multiplayer: boolean;
    invite_code: string | null;
    current_player_id: string | null;
  };
  is_my_turn?: boolean;
  player_id?: string;
}

export type WorldStats = Record<string, { entities: number; characters: number; scenarios: number; tags: string[] }>;

const INDEX_CACHE_TTL_MS = 3 * 60 * 1000;
const INDEX_CACHE_KEY_PREFIX = "idx_data_v1";

type IndexDataCache = {
  ts: number;
  payload: {
    worlds: WorldItem[];
    sharedWorlds: SharedWorldItem[];
    adventures: AdventureItem[];
    joinedAdventures: JoinedAdventureItem[];
    publicWorlds: PublicWorldItem[];
    trashedWorlds: WorldItem[];
    allWorlds: PublicWorldItem[];
    adventureWorlds: WorldItem[];
    worldStats: WorldStats;
    currentUserId: string | null;
  };
};

function removeAdventureFromWorldBuilderCache(worldId: string, adventureId: string) {
  const cacheKey = `wb_adventures:${worldId}`;
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.data)) return;
    const nextData = parsed.data.filter((a: any) => a?.id !== adventureId);
    sessionStorage.setItem(cacheKey, JSON.stringify({ ...parsed, ts: Date.now(), data: nextData }));
  } catch {
    // Ignore cache issues and keep runtime state as source of truth.
  }
}

// ─── Hook ───

export function useIndexData() {
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { isReady: authReady, user: authUser } = useAuthReady();
  const [worlds, setWorlds] = useState<WorldItem[]>([]);
  const [sharedWorlds, setSharedWorlds] = useState<SharedWorldItem[]>([]);
  const [adventures, setAdventures] = useState<AdventureItem[]>([]);
  const [joinedAdventures, setJoinedAdventures] = useState<JoinedAdventureItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"build" | "play" | "public" | "trash" | "admin">("build");
  const [publicWorlds, setPublicWorlds] = useState<PublicWorldItem[]>([]);
  const [trashedWorlds, setTrashedWorlds] = useState<WorldItem[]>([]);
  const [allWorlds, setAllWorlds] = useState<PublicWorldItem[]>([]);
  const [adventureWorlds, setAdventureWorlds] = useState<WorldItem[]>([]);
  const [cloningWorldId, setCloningWorldId] = useState<string | null>(null);
  const [worldStats, setWorldStats] = useState<WorldStats>({});

  // New World dialog
  const [showNewWorld, setShowNewWorld] = useState(false);
  const [creationMode, setCreationMode] = useState<"manual" | "prompt" | "template">("manual");
  const [newWorldName, setNewWorldName] = useState("");
  const [newWorldDesc, setNewWorldDesc] = useState("");
  const [newWorldPrompt, setNewWorldPrompt] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [contextFiles, setContextFiles] = useState<{ name: string; content: string }[]>([]);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-open creation dialog from URL params (e.g. from Explore page)
  const urlParamsChecked = useRef(false);
  useEffect(() => {
    if (urlParamsChecked.current) return;
    urlParamsChecked.current = true;
    const params = new URLSearchParams(window.location.search);
    if (params.get("create") === "true") {
      const tpl = params.get("template");
      if (tpl) {
        setCreationMode("template");
        setSelectedTemplate(tpl);
      }
      setShowNewWorld(true);
      // Clean URL
      params.delete("create");
      params.delete("template");
      const clean = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (clean ? `?${clean}` : ""));
    }
  }, []);

  // New Game dialog
  const [showNewGame, setShowNewGame] = useState(false);
  const [selectedWorldId, setSelectedWorldId] = useState<string>("");
  const [adventureName, setAdventureName] = useState("");
  const [isMultiplayer, setIsMultiplayer] = useState(false);
  const [turnMode, setTurnMode] = useState("free_form"); // canonical underscore form
  const [creatingGame, setCreatingGame] = useState(false);

  // Join dialog
  const [showJoin, setShowJoin] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);

  // Invite copy
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Credit tracking — single server-side aggregation covers both worlds and adventures
  const { summary: usageSummary } = useResourceUsageSummary();
  const worldCredits = usageSummary.worlds;
  const adventureCredits = usageSummary.adventures;

  // All playable worlds (own + shared with play/edit + public)
  const playableWorlds = useMemo(() => {
    const ids = new Set<string>();
    const result: any[] = [];
    for (const w of worlds) { ids.add(w.id); result.push(w); }
    for (const w of sharedWorlds) {
      if (!ids.has(w.id) && (w.permission === "play" || w.permission === "edit")) {
        ids.add(w.id); result.push(w);
      }
    }
    for (const w of publicWorlds) {
      if (!ids.has(w.id)) { ids.add(w.id); result.push(w); }
    }
    return result;
  }, [worlds, sharedWorlds, publicWorlds]);

  useEffect(() => {
    // Don't fire RLS-protected queries before the Supabase session has been
    // restored — otherwise the wave-1 parallel reads run anonymously and the
    // user sees empty dashboards / "loads multiple times" flicker.
    if (!authReady) return;
    if (!authUser) { navigate("/auth"); return; }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, authUser?.id]);

  const loadData = async () => {
    setIsLoading(true);
    let usedCachedData = false;
    try {
      const user = await getSessionUser();
      if (!user) { navigate("/auth"); return; }
      setCurrentUserId(user.id);
      const cacheKey = `${INDEX_CACHE_KEY_PREFIX}:${user.id}`;

      try {
        const raw = sessionStorage.getItem(cacheKey);
        if (raw) {
          const parsed = JSON.parse(raw) as IndexDataCache;
          if (parsed?.ts && Date.now() - parsed.ts < INDEX_CACHE_TTL_MS && parsed.payload) {
            setWorlds(parsed.payload.worlds || []);
            setSharedWorlds(parsed.payload.sharedWorlds || []);
            setAdventures(parsed.payload.adventures || []);
            setJoinedAdventures(parsed.payload.joinedAdventures || []);
            setPublicWorlds(parsed.payload.publicWorlds || []);
            setTrashedWorlds(parsed.payload.trashedWorlds || []);
            setAllWorlds(parsed.payload.allWorlds || []);
            setAdventureWorlds(parsed.payload.adventureWorlds || []);
            setWorldStats(parsed.payload.worldStats || {});
            usedCachedData = true;
            setIsLoading(false);
          }
        }
      } catch {
        // Ignore cache parse issues; network fetch below is source of truth.
      }

      // ── Wave 1: All independent queries in parallel ──
      const [worldsResult, adventuresResult, collabResult, trashResult, joinedResult, publicResult, rolesResult] = await Promise.all([
        supabase.from("worlds")
          .select("id, name, description, icon, setting, banner_url, allow_cloning, is_public, created_at, updated_at, user_id, deleted_at, config")
          .eq("user_id", user.id)
          .is("deleted_at", null)
          .order("updated_at", { ascending: false }),
        supabase.from("adventures")
          .select("id, name, world_id, created_at, updated_at, state, player_character, is_multiplayer, invite_code, current_player_id")
          .eq("user_id", user.id)
          .order("updated_at", { ascending: false })
          .limit(20),
        supabase.from("world_collaborators")
          .select("world_id, permission")
          .eq("user_id", user.id),
        supabase.from("worlds")
          .select("id, name, description, icon, setting, banner_url, allow_cloning, is_public, created_at, updated_at, user_id, deleted_at, config")
          .eq("user_id", user.id)
          .not("deleted_at", "is", null)
          .order("deleted_at", { ascending: false }),
        supabase.from("adventure_players")
          .select("id, adventure_id, user_id, is_host, character_name, adventure:adventures!inner(id, name, world_id, updated_at, state, player_character, is_multiplayer, invite_code, current_player_id)")
          .eq("user_id", user.id),
        supabase.from("worlds")
          .select("id, name, description, icon, setting, banner_url, created_at, updated_at, user_id, allow_cloning, config")
          .eq("is_public", true)
          .eq("is_confidential", false)
          .order("updated_at", { ascending: false })
          .limit(50),
        supabase.from("user_roles")
          .select("role")
          .eq("user_id", user.id),
      ]);

      // ── Set simple state immediately ──
      const ownedWorlds = (worldsResult.data || []) as any[];
      setWorlds(ownedWorlds);
      setTrashedWorlds((trashResult.data || []) as any[]);

      // Player counts for owned adventures
      const ownedAdvIds = (adventuresResult.data || []).map((a: any) => a.id);
      const playerCountPromise = ownedAdvIds.length > 0
        ? supabase.from("adventure_players").select("adventure_id").in("adventure_id", ownedAdvIds)
        : Promise.resolve({ data: [] });

      // Joined adventures
      const ownedIds = new Set(ownedAdvIds);
      const joined = (joinedResult.data || [])
        .filter((j: any) => !ownedIds.has(j.adventure_id))
        .map((j: any) => ({
          ...j,
          adventure: Array.isArray(j.adventure) ? j.adventure[0] : j.adventure,
          player_id: j.id,
          is_my_turn: false,
        }));
      for (const j of joined) {
        if (j.adventure?.current_player_id === j.id) j.is_my_turn = true;
      }
      setJoinedAdventures(joined as JoinedAdventureItem[]);

      // Shared worlds lookup
      const collabs = collabResult.data || [];
      const sharedWorldIds = collabs.map((c: any) => c.world_id);
      const sharedWorldPromise = sharedWorldIds.length > 0
        ? supabase.from("worlds").select("id, name, description, icon, setting, banner_url, created_at, updated_at, user_id, config").in("id", sharedWorldIds)
        : Promise.resolve({ data: [] });

      // Missing adventure worlds
      const allAdvWorldIds = [
        ...(adventuresResult.data || []).map((a: any) => a.world_id),
        ...joined.map((j: any) => j.adventure?.world_id).filter(Boolean),
      ];
      const knownWorldIds = new Set([
        ...ownedWorlds.map((w: any) => w.id),
        ...sharedWorldIds,
      ]);
      const missingWorldIds = [...new Set(allAdvWorldIds.filter((id: string) => !knownWorldIds.has(id)))];
      const missingWorldPromise = missingWorldIds.length > 0
        ? supabase.from("worlds").select("id, name, description, icon, setting, banner_url, created_at, updated_at, user_id, config").in("id", missingWorldIds)
        : Promise.resolve({ data: [] });

      // Admin worlds
      const isSuperAdmin = (rolesResult.data || []).some((r: any) => r.role === "superadmin");
      const adminWorldPromise = isSuperAdmin
        ? supabase.from("worlds")
            .select("id, name, description, icon, setting, banner_url, allow_cloning, is_public, created_at, updated_at, user_id, config")
            .is("deleted_at", null)
            .order("updated_at", { ascending: false })
            .limit(200)
        : Promise.resolve({ data: [] });

      // ── Wave 2: All dependent queries in parallel ──
      const [playerCountResult, sharedWorldResult, missingWorldResult, adminWorldResult] = await Promise.all([
        playerCountPromise,
        sharedWorldPromise,
        missingWorldPromise,
        adminWorldPromise,
      ]);

      // Set adventures with player counts
      const playerCountMap: Record<string, number> = {};
      for (const p of (playerCountResult as any).data || []) {
        playerCountMap[p.adventure_id] = (playerCountMap[p.adventure_id] || 0) + 1;
      }
      setAdventures((adventuresResult.data || []).map((a: any) => ({
        ...a,
        _player_count: playerCountMap[a.id] || 1,
      })) as any[]);

      // Set adventure worlds
      setAdventureWorlds(((missingWorldResult as any).data || []) as WorldItem[]);

      // Collect all owner IDs we need to resolve in one batch
      const publicWorldData = (publicResult.data || []) as any[];
      const sharedWorldData = ((sharedWorldResult as any).data || []) as any[];
      const adminWorldData = ((adminWorldResult as any).data || []) as any[];

      // Render lists immediately; owner names are enriched shortly after.
      if (sharedWorldData.length > 0) {
        const permMap: Record<string, string> = {};
        for (const c of collabs) permMap[(c as any).world_id] = (c as any).permission;
        setSharedWorlds(
          sharedWorldData.map((w: any) => ({
            ...w,
            permission: permMap[w.id] || "view",
            owner_name: "Unknown",
          }))
        );
      } else {
        setSharedWorlds([]);
      }

      if (publicWorldData.length > 0) {
        setPublicWorlds(
          publicWorldData.map((w: any) => ({
            ...w,
            owner_name: "Unknown",
            is_own: w.user_id === user.id,
          }))
        );
      } else {
        setPublicWorlds([]);
      }

      if (isSuperAdmin && adminWorldData.length > 0) {
        setAllWorlds(
          adminWorldData.map((w: any) => ({
            ...w,
            owner_name: "Unknown",
            is_own: w.user_id === user.id,
          }))
        );
      } else if (!isSuperAdmin) {
        setAllWorlds([]);
      }

      if (!usedCachedData) {
        setIsLoading(false);
      }

      const allOwnerIds = new Set<string>();
      for (const w of sharedWorldData) allOwnerIds.add(w.user_id);
      for (const w of publicWorldData) allOwnerIds.add(w.user_id);
      for (const w of adminWorldData) allOwnerIds.add(w.user_id);

      // ── Wave 3: Single profile lookup for all owners ──
      const ownerMap: Record<string, string> = {};
      const ownerIdArr = [...allOwnerIds];
      if (ownerIdArr.length > 0) {
        const { data: ownerProfiles } = await supabase
          .from("profiles").select("id, full_name").in("id", ownerIdArr);
        for (const p of ownerProfiles || []) ownerMap[p.id] = p.full_name || "Anonymous";
      }

      // Enrich shared worlds with owner names
      if (sharedWorldData.length > 0) {
        const permMap: Record<string, string> = {};
        for (const c of collabs) permMap[(c as any).world_id] = (c as any).permission;
        setSharedWorlds(
          sharedWorldData.map((w: any) => ({
            ...w,
            permission: permMap[w.id] || "view",
            owner_name: ownerMap[w.user_id] || "Unknown",
          }))
        );
      }

      // Enrich public worlds with owner names
      if (publicWorldData.length > 0) {
        setPublicWorlds(
          publicWorldData.map((w: any) => ({
            ...w,
            owner_name: ownerMap[w.user_id] || "Unknown",
            is_own: w.user_id === user.id,
          }))
        );
      }

      // Enrich admin worlds with owner names
      if (isSuperAdmin && adminWorldData.length > 0) {
        setAllWorlds(
          adminWorldData.map((w: any) => ({
            ...w,
            owner_name: ownerMap[w.user_id] || "Unknown",
            is_own: w.user_id === user.id,
          }))
        );
      }

      // ── World stats (background). Keep this light to avoid index load stalls. ──
      const allWorldIds = [
        ...ownedWorlds.map((w: any) => w.id),
        ...sharedWorldIds,
        ...publicWorldData.map((w: any) => w.id),
      ];
      const uniqueWorldIds = [...new Set(allWorldIds)];
      let statsForCache: WorldStats = {};
      if (uniqueWorldIds.length > 0) {
        const [entitiesRes, scenariosRes] = await Promise.all([
          supabase.from("entities")
            .select("world_id, type")
            .in("world_id", uniqueWorldIds)
            .is("deleted_at", null),
          supabase.from("scenarios")
            .select("world_id")
            .in("world_id", uniqueWorldIds),
        ]);

        const stats: WorldStats = {};
        for (const id of uniqueWorldIds) stats[id] = { entities: 0, characters: 0, scenarios: 0, tags: [] };
        for (const e of entitiesRes.data || []) {
          if (!stats[e.world_id]) continue;
          stats[e.world_id].entities++;
          if (e.type === "character" || e.type === "npc" || e.type === "player_character") stats[e.world_id].characters++;
        }
        for (const s of scenariosRes.data || []) { if (stats[s.world_id]) stats[s.world_id].scenarios++; }
        statsForCache = stats;
        setWorldStats(stats);
      }

      try {
        const cachePayload: IndexDataCache = {
          ts: Date.now(),
          payload: {
            worlds: ownedWorlds as WorldItem[],
            sharedWorlds: (sharedWorldData.length > 0
              ? sharedWorldData.map((w: any) => ({
                  ...w,
                  permission: (collabs.find((c: any) => c.world_id === w.id)?.permission || "view"),
                  owner_name: ownerMap[w.user_id] || "Unknown",
                }))
              : []) as SharedWorldItem[],
            adventures: ((adventuresResult.data || []).map((a: any) => ({
              ...a,
              _player_count: playerCountMap[a.id] || 1,
            })) as AdventureItem[]),
            joinedAdventures: joined as JoinedAdventureItem[],
            publicWorlds: (publicWorldData.length > 0
              ? publicWorldData.map((w: any) => ({
                  ...w,
                  owner_name: ownerMap[w.user_id] || "Unknown",
                  is_own: w.user_id === user.id,
                }))
              : []) as PublicWorldItem[],
            trashedWorlds: ((trashResult.data || []) as WorldItem[]),
            allWorlds: (isSuperAdmin
              ? adminWorldData.map((w: any) => ({
                  ...w,
                  owner_name: ownerMap[w.user_id] || "Unknown",
                  is_own: w.user_id === user.id,
                }))
              : []) as PublicWorldItem[],
            adventureWorlds: (((missingWorldResult as any).data || []) as WorldItem[]),
            worldStats: statsForCache,
            currentUserId: user.id,
          },
        };
        sessionStorage.setItem(cacheKey, JSON.stringify(cachePayload));
      } catch {
        // Ignore sessionStorage failures.
      }
    } catch {
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Handlers ───

  const handleFileUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newFiles: { name: string; content: string }[] = [];
    for (const file of Array.from(files)) {
      if (file.size > 2 * 1024 * 1024) { toast.error(`${file.name} is too large (max 2MB)`); continue; }
      try {
        const text = await file.text();
        newFiles.push({ name: file.name, content: text.substring(0, 50000) });
      } catch { toast.error(`Could not read ${file.name}`); }
    }
    setContextFiles((prev) => [...prev, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleCreateWorld = useCallback(async (templateOverride?: { name: string; desc: string; gameKey: string }) => {
    const isPromptMode = creationMode === "prompt";
    const isTemplateMode = creationMode === "template" || !!templateOverride;
    if (!isTemplateMode && !isPromptMode && !newWorldName.trim()) return;
    if (isPromptMode && !newWorldPrompt.trim()) return;
    setCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: creditData } = await supabase
        .from("user_credits").select("balance").eq("user_id", user.id).maybeSingle();
      if ((creditData?.balance ?? 0) < 50) {
        toast.error("You need at least 50 credits to create a world");
        setCreating(false);
        return;
      }

      let worldName: string;
      let worldDesc: string | null;
      let config: any = {};

      if (isTemplateMode && templateOverride) {
        // Generate a unique world name instead of using template name directly
        const suffix = Math.random().toString(36).substring(2, 6).toUpperCase();
        worldName = `${templateOverride.name} — ${suffix}`;
        worldDesc = templateOverride.desc;
        config = { template_key: templateOverride.gameKey, auto_generate: true, spoiler_free: true, language: locale, original_template_name: templateOverride.name };
      } else if (isPromptMode) {
        worldName = newWorldPrompt.trim().substring(0, 60) || "Untitled World";
        worldDesc = null;
        config = { creation_prompt: newWorldPrompt.trim() };
      } else {
        worldName = newWorldName.trim() || "Untitled World";
        worldDesc = newWorldDesc.trim() || null;
      }

      const { data, error } = await supabase.from("worlds")
        .insert({ name: worldName, description: worldDesc, user_id: user.id, config, context_files: contextFiles } as any)
        .select().single();
      if (error) throw error;

      if (contextFiles.length > 0) {
        for (const cf of contextFiles) {
          try {
            await supabase.functions.invoke("process-document", {
              body: {
                world_id: data.id, file_name: cf.name,
                file_type: cf.name.endsWith(".md") ? "markdown" : cf.name.endsWith(".json") ? "json" : "text",
                content: cf.content, existing_categories: [],
              },
            });
          } catch (e) { console.warn(`Failed to process context file ${cf.name}:`, e); }
        }
      }

      setShowNewWorld(false);
      setNewWorldName("");
      setNewWorldDesc("");
      setNewWorldPrompt("");
      setSelectedTemplate(null);
      setCreationMode("manual");
      setContextFiles([]);
      grantAchievement("first_world").catch(() => {});
      grantAchievement("five_worlds").catch(() => {});
      grantAchievement("ten_worlds").catch(() => {});

      // For template mode, navigate with auto_generate flag
      if (isTemplateMode && templateOverride) {
        navigate(`/world/${data.id}?auto_generate=true`);
      } else {
        navigate(`/world/${data.id}`);
      }
    } catch (err: any) {
      toast.error("Failed to create world: " + err.message);
    } finally {
      setCreating(false);
    }
  }, [creationMode, newWorldName, newWorldDesc, newWorldPrompt, contextFiles, navigate]);

  const handleDeleteWorld = useCallback(async (worldId: string) => {
    try {
      const { error } = await supabase.from("worlds").update({ deleted_at: new Date().toISOString() } as any).eq("id", worldId);
      if (error) throw error;
      const trashed = worlds.find((w) => w.id === worldId);
      setWorlds((prev) => prev.filter((w) => w.id !== worldId));
      if (trashed) setTrashedWorlds((prev) => [{ ...trashed, deleted_at: new Date().toISOString() } as any, ...prev]);
      toast.success("World moved to trash");
    } catch (err: any) {
      toast.error("Failed to delete: " + err.message);
    }
  }, [worlds]);

  const handleRestoreWorld = useCallback(async (worldId: string) => {
    try {
      const { error } = await supabase.from("worlds").update({ deleted_at: null } as any).eq("id", worldId);
      if (error) throw error;
      const restored = trashedWorlds.find((w) => w.id === worldId);
      setTrashedWorlds((prev) => prev.filter((w) => w.id !== worldId));
      if (restored) setWorlds((prev) => [{ ...restored, deleted_at: undefined } as any, ...prev]);
      toast.success("World restored");
    } catch (err: any) {
      toast.error("Failed to restore: " + err.message);
    }
  }, [trashedWorlds]);

  const handlePermanentDeleteWorld = useCallback(async (worldId: string) => {
    try {
      const { error } = await supabase.from("worlds").delete().eq("id", worldId);
      if (error) throw error;
      setTrashedWorlds((prev) => prev.filter((w) => w.id !== worldId));
      toast.success("World permanently deleted");
    } catch (err: any) {
      toast.error("Failed to delete: " + err.message);
    }
  }, []);

  const handlePublishWorld = useCallback(async (worldId: string, currentlyPublic: boolean) => {
    try {
      const { error } = await supabase.from("worlds").update({ is_public: !currentlyPublic } as any).eq("id", worldId);
      if (error) throw error;
      setWorlds((prev) => prev.map((w) => w.id === worldId ? { ...w, is_public: !currentlyPublic } : w));
      if (!currentlyPublic) {
        grantAchievement("publish_world").catch(() => {});
      }
      toast.success(currentlyPublic ? "World unpublished" : "World published!");
    } catch (err: any) {
      toast.error("Failed to update: " + err.message);
    }
  }, []);

  const handleCloneWorld = useCallback(async (worldId: string, worldName: string) => {
    setCloningWorldId(worldId);
    try {
      const { data, error } = await supabase.functions.invoke("clone-world", {
        body: { source_world_id: worldId, new_name: `${worldName} (My Copy)` },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      // Fetch the newly cloned world and add it to state immediately
      if (data?.new_world_id) {
        const { data: newWorld } = await supabase
          .from("worlds")
          .select("id, name, description, icon, setting, banner_url, allow_cloning, is_public, created_at, updated_at, user_id, config")
          .eq("id", data.new_world_id)
          .single();
        if (newWorld) {
          setWorlds((prev) => [newWorld as any, ...prev]);
        }
      }

      toast.success("World cloned successfully!");
    } catch (err: any) {
      toast.error("Failed to clone: " + (err.message || "Unknown error"));
    } finally {
      setCloningWorldId(null);
    }
  }, []);

  const handleDeleteAdventure = useCallback(async (adventureId: string) => {
    try {
      const ownAdventure = adventures.find((a) => a.id === adventureId);
      const joinedAdventure = joinedAdventures.find((j) => j.adventure_id === adventureId);
      const { error } = await supabase.from("adventures").delete().eq("id", adventureId);
      if (error) throw error;
      setAdventures((prev) => prev.filter((a) => a.id !== adventureId));
      setJoinedAdventures((prev) => prev.filter((j) => j.adventure_id !== adventureId));
      if (ownAdventure?.world_id) removeAdventureFromWorldBuilderCache(ownAdventure.world_id, adventureId);
      if (joinedAdventure?.adventure?.world_id) removeAdventureFromWorldBuilderCache(joinedAdventure.adventure.world_id, adventureId);
      toast.success("Adventure deleted");
    } catch (err: any) {
      toast.error("Failed to delete: " + err.message);
    }
  }, [adventures, joinedAdventures]);

  const handleLeaveAdventure = useCallback(async (adventureId: string) => {
    try {
      const joinedAdventure = joinedAdventures.find((j) => j.adventure_id === adventureId);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { error } = await supabase.from("adventure_players").delete().eq("adventure_id", adventureId).eq("user_id", user.id);
      if (error) throw error;
      setJoinedAdventures((prev) => prev.filter((j) => j.adventure_id !== adventureId));
      if (joinedAdventure?.adventure?.world_id) removeAdventureFromWorldBuilderCache(joinedAdventure.adventure.world_id, adventureId);
      toast.success("Left adventure");
    } catch (err: any) {
      toast.error("Failed to leave: " + err.message);
    }
  }, [joinedAdventures]);

  const handleStartGame = useCallback(async () => {
    if (!selectedWorldId || !adventureName.trim()) return;
    setCreatingGame(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data, error } = await supabase.from("adventures")
        .insert({
          world_id: selectedWorldId, user_id: user.id, name: adventureName.trim(),
          is_multiplayer: isMultiplayer, turn_mode: isMultiplayer ? turnMode : "free_form",
        })
        .select().single();
      if (error) throw error;
      if (isMultiplayer) {
        await supabase.from("adventure_players").insert({
          adventure_id: data.id, user_id: user.id, is_host: true, character_name: "",
        });
      }
      setShowNewGame(false);
      setAdventureName("");
      setSelectedWorldId("");
      setIsMultiplayer(false);
      // Track achievement
      grantAchievement("first_adventure").catch(() => {});
      grantAchievement("ten_adventures").catch(() => {});
      navigate(`/play/${data.id}`);
    } catch (err: any) {
      toast.error("Failed to start game: " + err.message);
    } finally {
      setCreatingGame(false);
    }
  }, [selectedWorldId, adventureName, isMultiplayer, turnMode, navigate]);

  const handleJoinGame = useCallback(async () => {
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      const { data: adv, error: findErr } = await supabase
        .from("adventures").select("id").eq("invite_code", joinCode.trim()).single();
      if (findErr || !adv) { toast.error("Invalid invite code"); setJoining(false); return; }
      const { data: existing } = await supabase
        .from("adventure_players").select("id").eq("adventure_id", adv.id).eq("user_id", user.id).maybeSingle();
      if (!existing) {
        const { error } = await supabase.from("adventure_players").insert({
          adventure_id: adv.id, user_id: user.id, is_host: false, character_name: "",
        });
        if (error) throw error;
      }
      toast.success("Joined adventure!");
      setShowJoin(false);
      setJoinCode("");
      navigate(`/play/${adv.id}`);
    } catch (err: any) {
      toast.error("Failed to join: " + err.message);
    } finally {
      setJoining(false);
    }
  }, [joinCode, navigate]);

  const copyInviteLink = useCallback((code: string, advId: string) => {
    navigator.clipboard.writeText(`${window.location.origin}/join/${code}`);
    setCopiedId(advId);
    toast.success("Invite link copied!");
    setTimeout(() => setCopiedId(null), 2000);
  }, []);

  return {
    // Data
    worlds, sharedWorlds, adventures, joinedAdventures,
    publicWorlds, trashedWorlds, allWorlds, adventureWorlds, worldStats,
    isLoading, activeTab, setActiveTab,
    currentUserId, cloningWorldId,
    playableWorlds,
    worldCredits, adventureCredits,

    // New World dialog state
    showNewWorld, setShowNewWorld,
    creationMode, setCreationMode,
    newWorldName, setNewWorldName,
    newWorldDesc, setNewWorldDesc,
    newWorldPrompt, setNewWorldPrompt,
    selectedTemplate, setSelectedTemplate,
    contextFiles, setContextFiles,
    creating, fileInputRef,

    // New Game dialog state
    showNewGame, setShowNewGame,
    selectedWorldId, setSelectedWorldId,
    adventureName, setAdventureName,
    isMultiplayer, setIsMultiplayer,
    turnMode, setTurnMode,
    creatingGame, setCreatingGame,

    // Join dialog state
    showJoin, setShowJoin,
    joinCode, setJoinCode,
    joining,

    // Invite
    copiedId,

    // Handlers
    handleFileUpload, handleCreateWorld,
    handleDeleteWorld, handleRestoreWorld, handlePermanentDeleteWorld,
    handlePublishWorld, handleCloneWorld,
    handleDeleteAdventure, handleLeaveAdventure,
    handleStartGame, handleJoinGame,
    copyInviteLink,
    navigate,
  };
}
